import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './App.css';

const MANIFEST_URL = '/docs-content/_index.json';
const CONTENT_BASE = '/docs-content/';

const linkPrefixSlash = (href) => {
  if (!href) return href;
  if (href.startsWith('#')) return href;
  return href;
};

const DocsPage = () => {
  const [manifest, setManifest] = useState(null);
  const [contents, setContents] = useState({});
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;

    const load = async () => {
      try {
        const mRes = await fetch(MANIFEST_URL, { cache: 'no-cache' });
        if (!mRes.ok) throw new Error(`Manifest fetch failed (${mRes.status})`);
        const m = await mRes.json();
        if (cancelled) return;
        setManifest(m);

        const fetched = await Promise.all(
          (m.sections || []).filter((s) => s.file).map(async (section) => {
            try {
              const r = await fetch(`${CONTENT_BASE}${section.file}`, { cache: 'no-cache' });
              if (!r.ok) throw new Error(`${section.file} (${r.status})`);
              const text = await r.text();
              return [section.id, text];
            } catch (err) {
              return [section.id, `# ${section.title}\n\n_Content failed to load: ${err.message}_`];
            }
          })
        );
        if (cancelled) return;
        const map = Object.fromEntries(fetched);
        setContents(map);
        setLoaded(true);
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!loaded) return;
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        const offset = 80;
        const elementPosition = el.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
      }
    }
  }, [loaded]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (!element) return;
    const offset = 80;
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', `#${id}`);
    }
  };

  const sections = manifest?.sections || [];

  // Sections carrying `href` are links out to a dedicated page (the per-feature
  // pages under /features) rather than markdown rendered inline here. Keeps the
  // docs nav complete without loading 40 more documents into one page.
  const navItemStyle = {
    display: 'block',
    width: '100%',
    padding: '7px 24px',
    textAlign: 'left',
    background: 'none',
    color: '#374151',
    fontSize: '0.87rem',
    transition: 'all 0.15s',
  };

  const markdownComponents = useMemo(() => ({
    a: ({ href, children, ...rest }) => (
      <a href={linkPrefixSlash(href)} {...rest}>{children}</a>
    ),
    table: ({ children, ...rest }) => (
      <div className="endpoint-table-wrapper">
        <table className="endpoint-table" {...rest}>{children}</table>
      </div>
    ),
    h1: ({ children }) => (
      <h2 style={{ fontSize: '2rem', fontWeight: 700, color: '#111827', margin: '0 0 24px' }}>{children}</h2>
    ),
    h2: ({ children }) => (
      <h3 style={{ fontSize: '1.35rem', fontWeight: 700, color: '#111827', margin: '32px 0 12px' }}>{children}</h3>
    ),
    h3: ({ children }) => (
      <h4 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#111827', margin: '24px 0 8px' }}>{children}</h4>
    ),
    p: ({ children }) => (
      <p style={{ color: '#374151', lineHeight: 1.7, marginBottom: '16px' }}>{children}</p>
    ),
    ul: ({ children }) => (
      <ul style={{ color: '#374151', lineHeight: 1.8, marginBottom: '16px', paddingLeft: '24px' }}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol style={{ color: '#374151', lineHeight: 1.8, marginBottom: '16px', paddingLeft: '24px' }}>{children}</ol>
    ),
    code: ({ inline, children, ...rest }) => (
      inline
        ? <code style={{ background: '#f3f4f6', padding: '1px 6px', borderRadius: '4px', fontSize: '0.9em', color: '#111827' }} {...rest}>{children}</code>
        : <code {...rest}>{children}</code>
    ),
    pre: ({ children }) => (
      <pre style={{
        background: '#0f172a',
        color: '#e2e8f0',
        padding: '16px',
        borderRadius: '8px',
        overflowX: 'auto',
        fontSize: '0.88rem',
        lineHeight: 1.5,
        marginBottom: '20px'
      }}>{children}</pre>
    ),
    blockquote: ({ children }) => (
      <blockquote style={{
        borderLeft: '3px solid #d1d5db',
        paddingLeft: '16px',
        color: '#4b5563',
        margin: '16px 0',
        fontStyle: 'italic'
      }}>{children}</blockquote>
    ),
  }), []);

  return (
    <div className="mp-page" style={{ padding: 0 }}>
      <div className="docs-layout" style={{ display: 'flex', minHeight: 'calc(100vh - 60px)' }}>
        {/* Sidebar */}
        <aside className="docs-sidebar" style={{
          width: '240px',
          background: '#f9fafb',
          borderRight: '1px solid #e5e7eb',
          padding: '32px 0',
          position: 'sticky',
          top: '60px',
          height: 'calc(100vh - 60px)',
          overflowY: 'auto',
          flexShrink: 0,
        }}>
          <div style={{ padding: '0 24px 20px', borderBottom: '1px solid #e5e7eb', marginBottom: '12px' }}>
            <h2 style={{ fontSize: '1rem', fontWeight: 800, color: '#111827', margin: 0 }}>mChatAI+</h2>
            <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: '4px 0 0' }}>Documentation</p>
            {manifest?.lastUpdated && (
              <p style={{ fontSize: '0.7rem', color: '#9ca3af', margin: '6px 0 0' }}>
                Updated {manifest.lastUpdated}
              </p>
            )}
          </div>
          <nav>
            {sections.map((section) => (
              <React.Fragment key={section.id}>
                {section.category && (
                  <div style={{
                    padding: '14px 24px 6px',
                    fontSize: '0.68rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: '#9ca3af',
                    fontWeight: 700
                  }}>
                    {section.category}
                  </div>
                )}
                {section.href ? (
                  <Link
                    to={section.href}
                    style={{ ...navItemStyle, textDecoration: 'none' }}
                    onMouseOver={(e) => { e.currentTarget.style.color = '#111827'; e.currentTarget.style.background = '#f3f4f6'; }}
                    onMouseOut={(e) => { e.currentTarget.style.color = '#374151'; e.currentTarget.style.background = 'none'; }}
                  >
                    {section.title}
                  </Link>
                ) : (
                  <button
                    onClick={() => scrollToSection(section.id)}
                    style={{ ...navItemStyle, border: 'none', cursor: 'pointer', outline: 'none' }}
                    onMouseOver={(e) => { e.target.style.color = '#111827'; e.target.style.background = '#f3f4f6'; }}
                    onMouseOut={(e) => { e.target.style.color = '#374151'; e.target.style.background = 'none'; }}
                  >
                    {section.title}
                  </button>
                )}
              </React.Fragment>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main ref={contentRef} className="docs-content" style={{
          flex: 1,
          maxWidth: '900px',
          padding: '64px 64px 128px',
          margin: '0'
        }}>
          {error && (
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              Failed to load documentation manifest: {error}
            </div>
          )}

          {manifest && (
            <div className="docs-hero" style={{ marginBottom: '64px' }}>
              {manifest.platformBadge && (
                <span className="trust-badge trust-official" style={{ marginBottom: '16px', display: 'inline-block' }}>
                  {manifest.platformBadge}
                </span>
              )}
              <h1 style={{ fontSize: '2.6rem', fontWeight: 800, color: '#111827', margin: '0 0 16px', lineHeight: 1.1 }}>
                {manifest.heroTitle || 'mChatAI+'}
              </h1>
              {manifest.heroSubtitle && (
                <p style={{ fontSize: '1.1rem', color: '#6b7280', lineHeight: 1.6, margin: 0 }}>
                  {manifest.heroSubtitle}
                </p>
              )}
            </div>
          )}

          {!loaded && !error && (
            <p style={{ color: '#6b7280' }}>Loading documentation…</p>
          )}

          {sections.filter((s) => !s.href).map((section, idx) => (
            <section
              key={section.id}
              id={section.id}
              className="docs-section"
              style={{
                marginBottom: '64px',
                paddingTop: idx === 0 ? 0 : '32px',
                borderTop: idx === 0 ? 'none' : '1px solid #e5e7eb'
              }}
            >
              {contents[section.id] != null ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {contents[section.id]}
                </ReactMarkdown>
              ) : (
                <h2 style={{ fontSize: '2rem', fontWeight: 700, color: '#111827', margin: '0 0 24px' }}>
                  {section.title}
                </h2>
              )}
            </section>
          ))}

          <footer style={{ marginTop: '96px', borderTop: '1px solid #e5e7eb', paddingTop: '32px', color: '#9ca3af', fontSize: '0.85rem' }}>
            <p>© {new Date().getFullYear()} mChatAI. All rights reserved.</p>
            <p>For additional help, visit the <a href="/hub" style={{ color: '#3b82f6', textDecoration: 'none' }}>Community Hub</a>.</p>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default DocsPage;
